/**
 * **Opening an encounter** — turning an `EncounterSetup` into the first tick's
 * `EncounterState`.
 *
 * Split out of `encounter.ts` along a seam that file already had: opening a
 * fight and stepping one are different jobs, and only the first has to know how
 * a spec becomes an entity. `encounter.ts` keeps the shapes, the tick and the
 * roster queries and re-exports `createEncounter`, so no consumer moved.
 *
 * The whole of what happens here is *derivation*: every field of the opening
 * state is a pure function of the setup, so two peers handed the same setup open
 * the same fight without exchanging a byte.
 */

import { fnv1a32 } from "../flight/hash.ts";
import { addVec3 } from "../flight/math3d.ts";
import { createRngBankState } from "../flight/rng.ts";
import { createSim, type SimState } from "../flight/state.ts";
import { createAiPilot } from "./ai-pilot.ts";
import { NOT_BURNING } from "./combat-damage.ts";
import type { CombatAircraft, EncounterAircraftSpec, EncounterSetup, EncounterState } from "./encounter.ts";
import { createFlakBattery } from "./flak.ts";
import { createRoute } from "./route.ts";
import { createShipBattery, shipBatteryId, shipBatterySpec, shipTargetSpec } from "./ship-pass.ts";
import { createTarget } from "./targets.ts";

/**
 * An aircraft that names its own seed keeps it — that is what makes a solo
 * encounter reproduce a bare `createSim` flight exactly, and what lets a
 * regression trace pin one aeroplane's randomness. Everything else is derived
 * `fnv1a32(id, encounterSeed)`, which is stable against roster order and gives
 * every aircraft its own physics streams.
 */
function spawn(spec: EncounterAircraftSpec, seed: number): SimState {
  const sim = createSim({ ...spec.flight, seed: spec.flight.seed ?? fnv1a32(spec.id, seed >>> 0) });
  if (!spec.offsetM) return sim;
  return { ...sim, position: addVec3(sim.position, spec.offsetM) };
}

function initialAircraft(spec: EncounterAircraftSpec, seed: number): CombatAircraft {
  return {
    id: spec.id,
    side: spec.side,
    sim: spawn(spec, seed),
    ai: spec.pilot.kind === "ai" ? createAiPilot(spec.pilot.skill) : null,
    fire: NOT_BURNING,
    status: "flying",
    fallingSeconds: 0,
    roundsFired: 0,
    hitsTaken: 0,
    gunHitsTaken: 0,
    hitsScored: 0,
    underFire: false,
    lastAttackerId: null,
    route: spec.waypoints ? createRoute(spec.waypoints) : null,
    routeOrders: spec.routeOrders ?? "engage",
    ammunition: spec.ammunition ?? "regular",
    bombsRemaining: spec.bombs?.count ?? 0,
    bombsReleased: 0,
    bombTargetIds: spec.bombs?.targetIds ?? [],
    lastReleaseTick: null,
    strikeTargetIds: spec.strikeTargetIds ?? [],
    quarryId: null,
  };
}

export function createEncounter(setup: EncounterSetup): EncounterState {
  const ships = setup.ships ?? [];
  // A ship arrives as one spec and becomes two entries: a hull on the target
  // list, which is what is shot at, drawn and hashed, and a battery on the flak
  // list, which is what shoots. Neither is authored by a caller — both are pure
  // functions of the spec (`ship-pass.ts`), so the two halves cannot drift.
  const targets = [...(setup.targets ?? []), ...ships.map(shipTargetSpec)];
  const flak = [...(setup.flak ?? []), ...ships.map(shipBatterySpec)];
  const ids = new Set([...setup.aircraft, ...targets, ...flak].map((spec) => spec.id));
  if (ids.size !== setup.aircraft.length + targets.length + flak.length) {
    throw new Error("encounter aircraft, target and flak ids must be unique");
  }
  return {
    seed: setup.seed,
    tick: 0,
    timeS: 0,
    aircraft: setup.aircraft.map((spec) => initialAircraft(spec, setup.seed)),
    targets: targets.map(createTarget),
    // A ship's battery carries `SHIP_FLAK_GUNS` slots rather than a field
    // battery's five, which is why it cannot go through `createFlakBattery`.
    flak: flak.map((spec) => {
      const ship = ships.find((entry) => shipBatteryId(entry.id) === spec.id);
      return ship ? createShipBattery(ship) : createFlakBattery(spec);
    }),
    ships,
    shipAims: [],
    rng: createRngBankState(setup.seed),
    rounds: [],
    bombs: [],
    bombImpacts: [],
    sceneryFires: [],
    crashSites: [],
    flakBursts: [],
    events: [],
    status: "active",
    combatLevel: setup.combatLevel,
  };
}
